import { Link, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { FaArrowLeft, FaArrowRight, FaCheck, FaMapMarkerAlt } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FloatingContactWidget from '../components/FloatingContactWidget';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import projectGroups from '../data/projectGroups';

const copy = {
    en: {
        back: 'Back to portfolio',
        completed: 'Completed project',
        location: 'Houston, Texas',
        overview: 'Project overview',
        services: 'Services provided',
        gallery: 'Project gallery',
        similar: 'Would you like a similar project for your business?',
        similarText: 'Tell us what you need and we will prepare a free, personalized quote.',
        quote: 'Request a free quote',
        notFound: 'Project not found',
        notFoundText: 'The project you are looking for is unavailable.'
    },
    es: {
        back: 'Volver al portafolio',
        completed: 'Proyecto realizado',
        location: 'Houston, Texas',
        overview: 'Resumen del proyecto',
        services: 'Servicios realizados',
        gallery: 'Galería del proyecto',
        similar: '¿Quieres un proyecto similar para tu negocio?',
        similarText: 'Cuéntanos qué necesitas y prepararemos una cotización personalizada gratis.',
        quote: 'Solicitar cotización gratis',
        notFound: 'Proyecto no encontrado',
        notFoundText: 'El proyecto que buscas no está disponible.'
    }
};

const ProjectDetailPage = () => {
    const { projectSlug } = useParams();
    const { language } = useLanguage();
    const { isDark } = useTheme();
    const text = copy[language] || copy.en;
    const project = projectGroups.find((item) => item.slug === projectSlug);

    useEffect(() => {
        if (!project) return undefined;

        const previousTitle = document.title;
        const previousMeta = {
            description: document.head.querySelector('meta[name="description"]')?.getAttribute('content') || '',
            ogTitle: document.head.querySelector('meta[property="og:title"]')?.getAttribute('content') || '',
            ogDescription: document.head.querySelector('meta[property="og:description"]')?.getAttribute('content') || '',
            ogUrl: document.head.querySelector('meta[property="og:url"]')?.getAttribute('content') || '',
            ogImage: document.head.querySelector('meta[property="og:image"]')?.getAttribute('content') || ''
        };
        const previousCanonical = document.head.querySelector('link[rel="canonical"]')?.getAttribute('href') || 'https://njdesignprintllc.com/';
        const title = `${project.title} - ${project.category[language]} in Houston | NJ Design & Print`;
        const description = project.summary[language];
        const canonicalUrl = `https://njdesignprintllc.com/portfolio/${project.slug}`;
        const previewImage = project.media[0].poster || project.media[0].src;

        const setMeta = (selector, attribute, value) => {
            let element = document.head.querySelector(selector);
            if (!element) {
                element = document.createElement('meta');
                const match = selector.match(/\[(name|property)="([^"]+)"\]/);
                if (match) element.setAttribute(match[1], match[2]);
                document.head.appendChild(element);
            }
            element.setAttribute(attribute, value);
        };

        document.title = title;
        setMeta('meta[name="description"]', 'content', description);
        setMeta('meta[property="og:title"]', 'content', title);
        setMeta('meta[property="og:description"]', 'content', description);
        setMeta('meta[property="og:url"]', 'content', canonicalUrl);
        setMeta('meta[property="og:image"]', 'content', `https://njdesignprintllc.com${previewImage}`);

        let canonical = document.head.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', canonicalUrl);

        return () => {
            document.title = previousTitle;
            setMeta('meta[name="description"]', 'content', previousMeta.description);
            setMeta('meta[property="og:title"]', 'content', previousMeta.ogTitle);
            setMeta('meta[property="og:description"]', 'content', previousMeta.ogDescription);
            setMeta('meta[property="og:url"]', 'content', previousMeta.ogUrl);
            setMeta('meta[property="og:image"]', 'content', previousMeta.ogImage);
            canonical.setAttribute('href', previousCanonical);
        };
    }, [project, language]);

    if (!project) {
        return (
            <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-white text-saratoga'}`}>
                <Navbar />
                <main className="pt-40 pb-24 px-4 text-center">
                    <h1 className="text-4xl font-bold mb-4">{text.notFound}</h1>
                    <p className="mb-8">{text.notFoundText}</p>
                    <Link to="/portfolio" className="inline-flex items-center gap-2 bg-atlantis text-black px-6 py-3 rounded-full font-bold"><FaArrowLeft /> {text.back}</Link>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${isDark ? 'bg-gray-950' : 'bg-white'}`}>
            <Navbar />
            <main>
                <header className="pt-32 pb-16 px-4 bg-gradient-to-br from-saratoga via-atlantis to-vida-loca text-white">
                    <div className="max-w-6xl mx-auto">
                        <Link to="/portfolio" className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-8"><FaArrowLeft /> {text.back}</Link>
                        <p className="font-bold tracking-widest text-sm mb-3">{text.completed}</p>
                        <h1 className="text-4xl md:text-6xl font-black mb-5">{project.title}</h1>
                        <div className="flex flex-wrap gap-3 text-sm font-semibold">
                            <span className="bg-black/25 px-4 py-2 rounded-full">{project.category[language]}</span>
                            <span className="inline-flex items-center gap-2 bg-black/25 px-4 py-2 rounded-full"><FaMapMarkerAlt /> {text.location}</span>
                        </div>
                    </div>
                </header>

                <section className="max-w-6xl mx-auto py-16 px-4 grid lg:grid-cols-3 gap-12">
                    <div className="lg:col-span-2">
                        <h2 className={`text-3xl font-bold mb-5 ${isDark ? 'text-white' : 'text-saratoga'}`}>{text.overview}</h2>
                        <p className={`text-lg leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{project.summary[language]}</p>
                    </div>
                    <aside className={`rounded-2xl p-7 ${isDark ? 'bg-gray-800' : 'bg-green-yellow-50'}`}>
                        <h2 className={`text-xl font-bold mb-5 ${isDark ? 'text-white' : 'text-saratoga'}`}>{text.services}</h2>
                        <ul className="space-y-3">
                            {project.services[language].map((service) => <li key={service} className={`flex items-start gap-3 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}><FaCheck className="text-atlantis mt-1 flex-none" /> {service}</li>)}
                        </ul>
                    </aside>
                </section>

                <section className={`py-16 px-4 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                    <div className="max-w-6xl mx-auto">
                        <h2 className={`text-3xl font-bold mb-8 ${isDark ? 'text-white' : 'text-saratoga'}`}>{text.gallery}</h2>
                        <div className={`grid gap-8 ${project.media.length > 1 ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
                            {project.media.map((media) => media.type === 'video' ? (
                                <video key={media.src} controls preload="metadata" poster={media.poster} className="w-full max-h-[680px] rounded-2xl bg-black shadow-xl" aria-label={media.alt}>
                                    <source src={media.src} type="video/mp4" />
                                </video>
                            ) : (
                                <img key={media.src} src={media.src} alt={media.alt} loading="lazy" width="1200" height="900" className="w-full max-h-[680px] object-contain rounded-2xl bg-black/5 shadow-xl" />
                            ))}
                        </div>
                    </div>
                </section>

                <section className="py-20 px-4 bg-gradient-to-r from-atlantis to-green-yellow-primary text-center">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-black text-black mb-4">{text.similar}</h2>
                        <p className="text-black/80 text-lg mb-8">{text.similarText}</p>
                        <Link to="/contact" className="inline-flex items-center gap-3 rounded-full bg-black text-white px-8 py-4 font-bold hover:bg-gray-900">{text.quote} <FaArrowRight /></Link>
                    </div>
                </section>
            </main>
            <Footer />
            <FloatingContactWidget />
        </div>
    );
};

export default ProjectDetailPage;
